import { Component, OnInit, Input, AfterViewInit, EventEmitter, Output, ViewChild, ElementRef, OnDestroy } from "@angular/core";
import { HttpResponse } from "@angular/common/http";

import { CurrencyPipe } from "@angular/common";

import { Observable, of, Subscription, combineLatest } from "rxjs";
import { tap, mergeMap, catchError } from "rxjs/operators";

import { PaymentService } from "../../../payments/services/payment.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DocumentManagementService } from "../../services/document-management.service";
import { LoaderService } from "../../services/loader.service";
import { GenerateReportModel } from "../../../reports/model/reports.model";
import { PaymentInstrument, PaymentStatus, PaymentTokenDisplay, PaymentType, ProofOfPurchaseTypes, writeToken, ApplicationPayment, Payment, AuthorizationResponse } from "../../../payments/models/paymentItem.model";
import { parseFileNameFromHeaders } from "../../models/onBaseIntegrationAndMapping.model";

@Component({
    selector: "receipt",
    templateUrl: "./receipt.component.html",
    styleUrls: ["./receipt.component.scss"],
    standalone: false
})
export class ReceiptComponent implements OnInit, AfterViewInit, OnDestroy {

    public allowReissue: boolean = false;
    public documentInformation: any;
    public loadingRecords: boolean = true;
    public _records: ApplicationPayment[];

    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);
    noDocumentDialog: Dialog = new Dialog("Unable to locate document", [new DialogButton("OK", false, () => { return; })]);
    invoiceFailedDialog: Dialog = new Dialog("Your payment invoice is not available for download at this time", [new DialogButton("OK", false, () => { return; })]);

    shrinkFontStyle = '<style> .shrink-font { font-size: 0.7rem; } </style>';

    @Input() public disableInvoice: boolean = false;
    @Input() public isSinglePaymentType: boolean = false;
    @Input() public applicationNumber: string;
    @Input() public authorization: AuthorizationResponse;
    @Input() public autoGeneratePdf: boolean = false;
    @Input() public hasPartials: boolean = false;
    @Input() public queueIdentifier: string = "";
    @Input() public isCredit: boolean = false;
    @Input() public isPaid: boolean = false;
    @Input() public paidDate: string;
    @Input() public shrinkFont: boolean = false;
    @Input() public showButtons: boolean = true;
    @Input() public unpaidRecordStatuses: PaymentStatus[] = [PaymentStatus.Pending, PaymentStatus.Overdue, PaymentStatus.Partial];
    @Input() public config: any;
    @Output() public receiptIssued = new EventEmitter<ApplicationPayment[]>();
    @ViewChild('downloadLink', { static: true }) public downloadLink: ElementRef;
    get address1(): string {
        return this.getResponseProperty("BillingAddressLine1");
    }

    get city(): string {
        return this.getResponseProperty("BillingCity");
    }

    get companyName(): string {
        let company = this.getResponseProperty("CompanyName");
        if (company.length === 0) {
            company = (this.records.length > 0) ? this.records[0].organization : "";
        }
        return company;
    }

    get dateAuthorized(): Date {
        const date = (this.paidDate) ? this.paidDate : this.getResponseProperty("DateAuthorized");
        return new Date(date);
    }

    get disableReceipt(): boolean {
        return this.autoGeneratePdf && (!this.authorization || this.authorization.Error === "True" || !this.documentInformation);
    }

    get disableDownloadInvoice(): boolean {
        return !this.documentInformation || this.disableInvoice;
    }

    get emailAddress(): string {
        return this.getResponseProperty("BillingEmail");
    }

    get filler(): string {
        return "..........................................................................................................................................................................................................................";
    }

    get hasAnyPaid(): boolean {
        if (!this.records || this.records.length === 0) return false;
        return !this.isPaid || this.records.findIndex(r => r.amountPaid !== 0) !== -1;
    }

    get orderNumber(): string {
        return this.getResponseProperty("Number");
    }

    get owner(): string {
        if (this.records && this.records.length > 0)
            return this.records[0].organizationId + ' - ' + this.records[0].organization;
    }

    get paymentTypes(): string[] {
        return this.getPaymentTypes();
    }

    get phoneNumber(): string {
        return this.getResponseProperty("BillingPhoneNumber");
    }

    get records(): ApplicationPayment[] {
        return this._records;
    }

    @Input() set records(value: ApplicationPayment[]) {
        this._records = value;
        if (value != null) {
            const existingReceipt = this.records.find(r => !!r.queuedReceipt);
            if (existingReceipt && this.isCredit) {
                this.documentInformation = existingReceipt.queuedReceipt;
            }
            const existingInvoice = this.records.find(r => !!r.queuedInvoice);
            if (existingInvoice && !this.isCredit) {
                this.documentInformation = existingInvoice.queuedInvoice;
            }
        }
        this.loadingRecords = (value == null);
    }

    get remainingDue(): string {
        const amt = this.getTotalDue() - this.getTotalPaid();
        return this.pipe.transform(amt, "USD", "symbol", "1.2-2");
    }

    get state(): string {
        return this.getResponseProperty("BillingState");
    }

    get totalPaid(): string {
        return this.pipe.transform(this.getTotalPaid(), "USD", "symbol", "1.2-2");
    }

    get totalDue(): string {
        return this.pipe.transform(this.getTotalDue(), "USD", "symbol", "1.2-2");
    }

    get zipCode(): string {
        return this.getResponseProperty("BillingZip");
    }

    subscription: Subscription = new Subscription();

    constructor(
        private readonly paymentService: PaymentService,
        private readonly pipe: CurrencyPipe,
        private readonly loader: LoaderService,
        readonly dialogService: DynamicDialogService,
        private documentService: DocumentManagementService
    ) { }

    ngOnInit() {
   
    }

    ngAfterViewInit() {
        //get passed the appropriate document type somehow?
        if (this.autoGeneratePdf) {
            this.autoGenerateDocument();
        }
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    autoGenerateDocument(): void {
        let generateDocument = false;
        if (this.isPaid) {
            generateDocument = this._records.findIndex(r => !r.receipt && !r.queuedReceipt) !== -1;
        } else {
            generateDocument = this._records.findIndex(r => !r.invoice && !r.queuedInvoice) !== -1;
        }
        if (generateDocument) {
            this.loader.toggleLoader(true);
            let data$ = this.generatePdf().pipe(
                tap((data: string) => {
                    this.documentInformation = data;
                }),
                mergeMap((data: string) => {
                    return this.updateRecords();
                }),
                catchError(err => {
                    this.handleError(err);
                    return this.updateRecords();
                })
            ).subscribe(
                (data: ApplicationPayment[]) => {
                    this.loader.toggleLoader(false);
                    this.receiptIssued.emit(data);
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                }
            );
            this.subscription.add(data$);
        } else {
            if (this.isPaid) {
                let record = this._records.find(r => r.receipt != null);
                if (record == null) {
                    record = this._records.find(r => r.queuedReceipt != null);
                    this.documentInformation = record.queuedReceipt;
                } else {
                    this.documentInformation = record.receipt;
                }
            } else {
                let record = this._records.find(r => r.invoice != null);
                if (record == null) {
                    record = this._records.find(r => r.queuedInvoice != null);
                    this.documentInformation = record.queuedInvoice;
                } else {
                    this.documentInformation = record.invoice;
                }
            }
        }
    }

    download(): void {        
        this.loader.toggleLoader(true);
        let subs$ = this.getDownloadObservable().subscribe(
            (data: HttpResponse<Blob>) => {
                this.loader.toggleLoader(false);
                if (data == null) this.noDocument();
            }, 
            (error) => {
                this.handleError(error);
            }
        );
        this.subscription.add(subs$);
    }

    generatePdf(): Observable<string> {
        const documentType = this.isPaid ? ProofOfPurchaseTypes.Receipt : ProofOfPurchaseTypes.Invoice;
        const content = this.getContent(documentType);
            if (content && content.innerHTML) {
                const model = new GenerateReportModel();
                model.html = `${this.shrinkFontStyle} ${content.innerHTML}`;
                model.organizationId = this.records[0].organizationId;
                model.organizationName = this.records[0].organization;
                model.applicationNumber = this.applicationNumber;
                model.useBanner = true;
                model.formatAsLetter = true;
                model.usePageNumberHeader = false;
                model.styleMergeFields = false;
                model.documentTypeName = (this.isPaid) ? this.records[0].receiptDocumentType : this.records[0].invoiceDocumentType;
                model.flowId = this.records[0].workflowId;
            return this.paymentService.getPdf(model, documentType);
        }

        return of(null);
    }

    getContent(documentType: ProofOfPurchaseTypes): HTMLElement {
        const contentSrc = this.getDocumentId(documentType);
        const content = document.getElementById(contentSrc);
        return content;
    }

    getDisplayTokens(tokens: string[]): PaymentTokenDisplay[] {
        const display: PaymentTokenDisplay[] = [];
        if (this.isSinglePaymentType && this.authorization != null) {
            let token: string = tokens[0];
            if (this.authorization.PaymentType == PaymentType.Credit) {
                token = `Credit Authorization #${this.authorization.Token} in the amount of ${this.pipe.transform(this.authorization.Amount)}`;
            } else if (this.authorization.PaymentType == PaymentType.ACH) {
                token = token = `Check #${this.authorization.Token} in the amount of ${this.pipe.transform(this.authorization.Amount)}`;
            }
            display.push(new PaymentTokenDisplay(token));
        } else if (this.isSinglePaymentType && tokens.length > 0)
            display.push(new PaymentTokenDisplay(tokens[0]));
        else
            tokens.forEach(t => display.push(new PaymentTokenDisplay(t)));
        return display;
    }

    getDocumentId(docType: string): string {
        return `${this.applicationNumber}_${docType}`;
    }

    getDownloadObservable(): Observable<HttpResponse<Blob>> {
        let documentObservable: Observable<string | number>;
        if (this.isPaid == true && this.isCredit == false && this.documentInformation != null) this.documentInformation = null;
        if ((!this.autoGeneratePdf && !this.documentInformation) || this.allowReissue) {
            this.loader.toggleLoader(true);
            documentObservable = this.generatePdf();
        } else {
            documentObservable = of(this.documentInformation);
        }

        const loadObs$ = documentObservable.pipe(
            tap((id: string | number) => {
                if (!this.autoGeneratePdf) {
                    this.documentInformation = id;
                }
            }),
            mergeMap((id: string | number) => {
                return this.updateRecords()
            }),
            mergeMap((records: ApplicationPayment[]) => {                
                this.receiptIssued.emit(records);
                return this.documentService.downloadFromQueue(this.documentInformation);
            }),
            tap((data) => {
                if (data != null) {
                    const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                    const blob = new Blob([data.body], { type: data.body.type });
					const blobUri = window.URL.createObjectURL(blob);
					const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
					anchor.href = blobUri;
					anchor.download = fileName;
					anchor.click();
                }
            }));
        return loadObs$;
    }

    getPaymentNumber(payment: ApplicationPayment): string {
        if (payment.id) return payment.id;
        return this.filler;
    }

    getPaymentTypes(): string[] {
        const list: string[] = [];
        if (this.records) {
            for (let r = 0; r < this.records.length; r++) {
                if (this.records[r].amountPaid !== 0 && this.records[r].paymentRecords != null) {
                    if (this.records[r].amountDue == 0 && this.records[r].paymentStatus != PaymentStatus.Paid) this.records[r].paymentStatus = PaymentStatus.Paid;
                    for (let p = 0; p < this.records[r].paymentRecords.length; p++) {
                        let token = this.records[r].paymentRecords[p].token;
                        if (token == null) {
                            if (this.records[r].paymentRecords[p].paymentType != null) {
                                let paymentType = this.records[r].paymentRecords[p].paymentType;
                                switch (paymentType) {
                                    case PaymentType.PaidCash:
                                        token = `Cash in the amount of ${this.pipe.transform(this.records[r].paymentRecords[p].amount)}`;
                                        break;
                                    case PaymentType.LocalCheck:
                                    case PaymentType.ACH:
                                        token = `Check #${this.records[r].paymentRecords[p].tokenId} in the amount of ${this.pipe.transform(this.records[r].paymentRecords[p].amount)}`;
                                        break;
                                    case PaymentType.MoneyOrder:
                                        token = `Money Order #${this.records[r].paymentRecords[p].tokenId} in the amount of ${this.pipe.transform(this.records[r].paymentRecords[p].amount)}`;
                                        break;
                                    case PaymentType.Credit:
                                        token = `Credit Authorization #${this.records[r].paymentRecords[p].tokenId} in the amount of ${this.pipe.transform(this.records[r].paymentRecords[p].amount)}`;
                                        break;
                                }                                
                            } else { token = "" } //default case to keep component from blowing up on a null reference
                            
                        }
                        
                        list.push(token);
                    }
                }
            }
        }

        return list;
    }

    getResponseProperty(property: string): string {
        if (!this.authorization) return "";
        let data = this.authorization[property];
        if (data === undefined || data === null) {
            const camelcasedProperty = `${property.substring(0, 1).toLocaleLowerCase()}${property.substring(1, property.length)}`;
            data = this.authorization[camelcasedProperty];
        }
        if (data === undefined || data === null) return "";
        data = data.split('+').join(' ');
        return data;
    }

    getTotalDue(): number {
        let value = 0;
        this.records.forEach(r => {
            const amt = r.fee;// parseFloat(r.fee);
            value = value + amt;
        });
        return value;
    }

    getTotalPaid(): number {
        let value = 0;
        for (let r = 0; r < this.records.length; r++) {
            value = value + this.records[r].amountPaid;
        }
        return value;
    }

    handleCashTokens(listItem: string, newItem: string): string {
        const listParts = listItem.split("$");
        const listAmount = Number(listParts[1]);
        const itemParts = newItem.split("$");
        const itemAmount = Number(itemParts[1]);
        const totalAmt = listAmount + itemAmount;
        return writeToken(totalAmt, { id: "", instrumentType: "Cash", instrumentAmount: totalAmt, appliedAmount: totalAmt, token: "" } as PaymentInstrument);
    }

    handleError(error: any): void {
        console.error(error);
        this.loader.toggleLoader(false);
        if (error.status === 400 && error.url.includes("generateInvoice")) {
            this.handleFailedInvoiceGeneration()
        } else {
            this.errorDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.errorDialog);
        }
    }

    handleFailedInvoiceGeneration(): void {
        this.invoiceFailedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.invoiceFailedDialog);
        if (this.records && this.records.length > 0 && this.config) {
            if (this.config.invoiceType && this.config.invoiceType.name && !this.records[0].invoiceDocumentType) {
                this.records[0].invoiceDocumentType = this.config.invoiceType.name;
            }
            if (this.config.receiptType && this.config.receiptType.name && !this.records[0].receiptDocumentType) {
                this.records[0].receiptDocumentType = this.config.receiptType.name;
            }
        }
    }
   
    handleOtherTokens(listItem: string, newItem: string, type: string): string {
        const listItemParts = listItem.split(" ");
        const newItemParts = newItem.split(" ");
        const listNumberPart = listItemParts.find(i => i.indexOf("#") !== -1);
        const newNumberPart = newItemParts.find(i => i.indexOf("#") !== -1);
        if (!listNumberPart || !newNumberPart) return newItem;
        const listValueParts = listItemParts.filter(i => i.indexOf("$") !== -1);
        const newValueParts = newItemParts.filter(i => i.indexOf("$") !== -1);
        const listInstrumentValue = Number(listValueParts[0].replace("$", ""));
        const newInstrumentValue = Number(newValueParts[0].replace("$", ""));
        if (listInstrumentValue !== newInstrumentValue) return newItem;
        const listValueApplied = Number(listValueParts[1].replace("$", ""));
        const newValueApplied = Number(newValueParts[1].replace("$", ""));
        const totalApplied = listValueApplied + newValueApplied;
        if (totalApplied > listInstrumentValue) return newItem;
        return writeToken(totalApplied,
            {
            id: "",
            instrumentType: type,
            instrumentAmount: listInstrumentValue,
            appliedAmount: totalApplied,
            token: listNumberPart
            } as PaymentInstrument);
    }

    issueReceipt(): void {
        let unpaid = this.records.filter(r => this.unpaidRecordStatuses.findIndex(f => r.paymentStatus === f) !== -1);
        if (unpaid.length > 0) {
            let dialog: Dialog = new Dialog(`Application ${this.applicationNumber} has some payments due that have not yet been satisfied. The receipt will need to be reissued once payment has been received on all accounts. Do you wish to continue?`, [new DialogButton("Yes", true, () => this.onDialogConfirm()), new DialogButton("No", false, () => this.onDialogClose())]);
            dialog.toggleDialog(true);
            this.dialogService.updateDialog(dialog);
        } else {
            this.updateAndDownload(false);
        }
    }

    noDocument(): void {
        this.noDocumentDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.noDocumentDialog);
    }

    updateAndDownload(reissue: boolean): void {
        const downloadObs$ = this.getDownloadObservable();
        const subs$ = downloadObs$.pipe(         
        ).subscribe(
            (data: HttpResponse<Blob>) => {
                this.loader.toggleLoader(false);
                if (data == null) this.noDocument();
                this.allowReissue = reissue;
            },
            (error) => {
                this.handleError(error);
            }
        );
        this.subscription.add(subs$);     
    }


    updateRecords(): Observable<ApplicationPayment[]> {
        const updateObservables: Observable<ApplicationPayment>[] = [];
        for (let r = 0; r < this.records.length; r++) {
            if (this.isPaid) {                
                if (this.documentInformation != null && isNaN(this.documentInformation)) {
                    this.records[r].queuedReceipt = this.documentInformation.toString();
                } else {
                    this.records[r].receipt = this.documentInformation;
                }
                if (this.records[r].paymentStatus == PaymentStatus.PendingReceipt)
                    this.records[r].paymentStatus = PaymentStatus.Paid;
            }
            else {
                if (this.documentInformation != null && isNaN(this.documentInformation)) {
                    this.records[r].queuedInvoice = this.documentInformation.toString();
                } else {
                    this.records[r].invoice = this.documentInformation;
                }
            }
            if (this.records[r].feeType == null) this.records[r].feeType = this.records[r].description;
            updateObservables.push(this.paymentService.savePaymentRecord(this.records[r]).pipe(
                tap((savedRecord: ApplicationPayment) => {
                    let index = this.records.findIndex(r => r.id == savedRecord.id);
                    if (index != -1) {
                        this.records.splice(index, 1, savedRecord); //replace the existing record in the collection with the saved one, in order to track the associated payments for next go round, in case it was a partial payment
                    }
                })
)           );
        }
        return combineLatest(updateObservables);
    }

    public onDialogConfirm(): void {
        this.updateAndDownload(true);
    }

    public onDialogClose(): void {
        console.log("Download cancelled");
    }

}
